import { i18n as i18nInstance } from 'i18next';
import _ from 'lodash';
import { useEffect, useLayoutEffect, useMemo, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import {
  AuthorizableRouteObject,
  Channel,
  Envs,
  LayoutComponentType,
  MenuItem,
  PageContainer,
  RouterHistoryEntry,
  useAppContext,
  useChannelContext,
  useEnvContext,
  useFindMenuTitle,
  useLocale,
  useMenuAuthority,
  useMenuStore,
  usePageAttributesStore,
  useQuery,
  useRestContext,
  useRouteDataStorage,
  useRouterHistory,
  useRouterRegistry,
  useSecureContext,
} from 'scbk-services';
import { ErrorComponent } from 'scbk-shared-module/error';
import { Loading } from 'scbk-ui-web';
import { usePageLayoutStore } from '../stores/usePageLayoutStore';
import { EmptyLayout } from './layout/EmptyLayout';
import { MainLayout } from './layout/MainLayout';
import { PageLayout } from './layout/PageLayout';

const layouts: Record<string, LayoutComponentType> = {
  page: PageLayout,
  main: MainLayout,
} as const;

interface RootLayoutProps {
  i18n: i18nInstance;
}

/**
 * location url 정보에서 화면 id를 추출한다.
 * ex) /[모듈명]/[L1]/TEST_ID => 화면ID: TEST_ID
 * @param pathname
 * @returns
 */
const extractRouteInfoFromLocation = (url: string): AuthorizableRouteObject | null | undefined => {
  if (!url) {
    return;
  }

  const parsed = new URL(url, window.location.origin);
  const pathname = parsed.pathname;
  const arr = pathname.split('/').filter(Boolean);
  if (!arr || arr.length === 0) {
    return;
  }

  return {
    module: arr[0] as string,
    id: arr[arr.length - 1] as string,
    fullPath: pathname,
  } as AuthorizableRouteObject;
};

// 현재 pathname
// url 입력해서 진입시 1회만 checkRoute 하도록 수정
let currentPathStr = '';

/**
 * 화면 레이아웃 처리 컴포넌트 Route 최상위 element로 등록한다.
 */
const RootLayout = ({ i18n }: RootLayoutProps) => {
  const { t } = useTranslation();
  const { findByPath } = useMenuStore.getState(); // 메뉴 정보 state
  const menuInit = useMenuStore((state) => state.initialized);
  const currentRouteObjectConfig = useRouterRegistry((state) => state.currentRouteObjectConfig); // 현재 routeObject
  const { reset: historyReset, size: historySize, storageKey } = useRouterHistory.getState(); // routerHistory state
  const { get: getMenuRouteData, remove: removeMenuRouteData } = useRouteDataStorage();
  const { findModuleRouteObjectsByPageId } = useRouterRegistry();
  const setCurrentMenuItem = useMenuStore((state) => state.setCurrentMenuItem); // 현재 menu set
  const { updateContext: updateRestContext } = useRestContext();

  const [layoutOptions, setLayoutOptions] = useState<Record<string, unknown>>({});
  const [isNotFound, setIsNotFound] = useState<boolean>(false);
  //const { checkRoute } = useAccessCheckRoute(); // route CDD, 외부 url 호출
  const { checkMenuInfo } = useMenuAuthority(true);
  const { darkMode } = useAppContext();
  const { query } = useQuery();

  const { getLocale } = useLocale();

  const { resetKeypadPlainMapSecureContext } = useSecureContext();
  const { channel } = useChannelContext();

  const { title: dynamicTitle, options: dynamicLayoutOptions } = usePageLayoutStore((state) => {
    return {
      title: state.title,
      options: state.options,
    };
  });

  // document title
  const { findMenuTitle } = useFindMenuTitle();

  const { set: setPageAttribute } = usePageAttributesStore();

  const { getEnv } = useEnvContext();

  /**
   * 레이아웃을 확정한다.
   */
  const Layout = useMemo(() => {
    const layoutType = (layoutOptions['type'] ?? '') as string;

    //레이아웃타입을 지정하지 않은경우
    if (!layoutType) {
      return EmptyLayout;
    }
    return layouts[layoutType];
  }, [layoutOptions]);

  /**
   * 페이지 타이틀 다국어 처리
   */
  const title = useMemo(() => {
    const env = getEnv();
    const envText =
      env === Envs.PROD || channel === Channel.IB
        ? ''
        : ` [${env === Envs.DEV ? 'UT' : (env ?? '')?.toUpperCase()}]`;
    //***************************************
    //업무화면에서 동적으로 설정한 타이틀을 우선으로 처리
    //***************************************
    if (dynamicTitle) {
      return dynamicTitle + envText;
    }

    if (!layoutOptions) {
      return '';
    }

    const title = layoutOptions['title'] as string;
    //디폴트값을 국문 타이틀로 설정하도록 수정
    const i18nTitle = t(layoutOptions['titleKey'] as string, title) as string;

    return i18nTitle === null || i18nTitle === '' ? title + envText : i18nTitle + envText;
  }, [dynamicTitle, i18n, layoutOptions, getEnv]);

  /**
   * 페이지 레이아웃 옵션 merge
   * withPageComponent layoutOptions + usePageLayout layoutOption
   */
  const mergedLayoutOptions = useMemo(() => {
    return {
      ...(layoutOptions ?? {}),
      ...(dynamicLayoutOptions ?? {}),
    };
  }, [layoutOptions, dynamicLayoutOptions]);

  const _props = useMemo(() => {
    return {
      ...mergedLayoutOptions,
      darkMode: darkMode === 'Y',
      title,
    };
  }, [mergedLayoutOptions, darkMode, title]);

  const initStackRef = useRef<boolean>(false);

  /**
   *
   * @param id : route id
   * @param module
   * @param path : route path
   * @returns
   */
  const initStack = async (id: string, currentMenuInfo: MenuItem | null | undefined) => {
    if (initStackRef.current) {
      return;
    }

    if (!currentMenuInfo) {
      console.log('#### 메뉴없음');
      initStackRef.current = true;
      setIsNotFound(true);
      return;
    }

    console.log('### RootLayout checkMenu ###');
    const checkedMenu = await checkMenuInfo(currentMenuInfo);
    if (!checkedMenu) {
      return;
    }

    console.log('### localStorage.getItem(STORAGE_KEY) : ', localStorage.getItem(storageKey));

    const stored = localStorage.getItem(storageKey);
    let originHistoryStack: RouterHistoryEntry[] = [];
    if (stored) {
      const parsed = JSON.parse(stored);
      if (Array.isArray(parsed)) {
        originHistoryStack = [...parsed];
      }
    }

    if (historySize() === 0) {
      let routeData = {};

      const { id: menuId, url: menuPath } = currentMenuInfo;
      // title 설정
      document.title = findMenuTitle(menuId) ?? 'SC Bank';

      const historyStackOrigin = originHistoryStack?.filter((item) => item.path === menuPath)?.[0];

      if (historyStackOrigin) {
        routeData = { ...historyStackOrigin.routeData };
      }

      //changeViewService 값이 있는지 확인 후 routeObject 정보를 가져온다.
      let resolvedRouteObject = findModuleRouteObjectsByPageId(id);
      if (!_.isNil(query['changeViewService']) && !_.isEmpty(query['changeViewService'])) {
        const changeViewService = query['changeViewService'] ?? '';
        resolvedRouteObject = findModuleRouteObjectsByPageId(changeViewService);

        //routeObject 찾은 후 changeViewService 삭제
        delete query['changeViewService'];
      }

      if (!resolvedRouteObject) {
        return;
      }

      try {
        if (query['orgWebviewType'] === 'v3') {
          routeData = { ...routeData, ...query };
        } else {
          routeData = {
            ...routeData,
            ...(getMenuRouteData(menuId) ?? {}),
            //query,
          };
        }

        //최초 진입 시 firstPage 속성 추가
        setPageAttribute(resolvedRouteObject.id ?? id, { firstPage: 'Y' });

        const entry: RouterHistoryEntry = {
          id: resolvedRouteObject.id ?? id,
          module: resolvedRouteObject.module,
          path: resolvedRouteObject.fullPath ?? '',
          routeData,
          routeType: 'menu',
          query: window.location.search.replace(/^\?/, ''),
        };

        historyReset(entry);
      } finally {
        //로컬스토리지에서 routeData 획득 이후 삭제
        removeMenuRouteData(menuId);
      }
    }

    initStackRef.current = true;
  };

  /**
   * 현재 경로를 가져온다.
   */
  const currentPath = useMemo((): string => {
    return `${location.pathname}${location.search ?? ''}${location.hash ?? ''}`;
  }, [location.pathname, location.search, location.hash]);

  // 첫 진입 시 route 처리 useEffect
  useEffect(() => {
    if (currentPath === '' || currentPath === '/') {
      return;
    }

    if (currentPathStr === currentPath) {
      return;
    }

    //메뉴 초기화가 안되었을 경우
    if (!menuInit) {
      return;
    }

    let resolvedRouteObject = extractRouteInfoFromLocation(currentPath);

    //첫 진입 처리 : 주소창에 url 입력하고 진입 시
    if (resolvedRouteObject) {
      // return;
      currentPathStr = currentPath;

      const { id = '', fullPath = '' } = resolvedRouteObject;

      const currentMenu = findByPath(fullPath);

      if (currentMenu) {
        setCurrentMenuItem(currentMenu);

        const { id: menuId, fields = {} } = currentMenu;
        const acType = fields ? fields['acCheckType'] : '';

        const menuInfo = {
          menuId,
          acType,
        };

        updateRestContext({ ...menuInfo });
      }

      const currentMenuInfo = findByPath(fullPath);

      initStack(id, currentMenuInfo);
    }
  }, [currentPath, menuInit, setCurrentMenuItem, query]);

  useLayoutEffect(() => {
    if (!currentRouteObjectConfig?.meta) {
      return;
    }
    setLayoutOptions({ ...(currentRouteObjectConfig?.meta ?? {}) });

    // 키패드 평문 정보 삭제
    resetKeypadPlainMapSecureContext();
  }, [currentRouteObjectConfig?.meta, getLocale()]);

  return (
    <Layout {..._props}>
      {isNotFound ? (
        <ErrorComponent errorCode="404" />
      ) : (
        <PageContainer loadingComponent={Loading} />
      )}
    </Layout>
  );
};

export { RootLayout, type RootLayoutProps };
